import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, X, Smartphone, ArrowRight, Send, CheckCircle, AlertCircle } from 'lucide-react';
import logo from '../../assets/layout/logo.png';
import partnerLogo from '../../assets/layout/partnerlogo.png';
import freightLogo from '../../assets/layout/freightlogo.png';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [logoErrors, setLogoErrors] = useState({});

  const navigationItems = useMemo(() => [
    {
      name: 'Company',
      href: '/company',
      id: 'company',
      ariaLabel: 'Learn about RoadExpress company and our mission'
    },
    {
      name: 'Shippers',
      href: '/shippers',
      id: 'shipper',
      ariaLabel: 'Explore services for shippers and logistics solutions'
    },
    {
      name: 'Carriers',
      href: '/carriers',
      id: 'carrier',
      ariaLabel: 'Join as a carrier and access freight opportunities'
    },
    {
      name: 'Support',
      href: '/support',
      id: 'support',
      ariaLabel: 'Get help and customer support'
    },
    {
      name: 'Career',
      href: '/career',
      id: 'career',
      ariaLabel: 'Explore career opportunities at RoadExpress'
    },
  ], []);

  const appOptions = [
    {
      id: 1,
      name: 'RoadExpress Partner',
      description: 'For carriers and transport partners',
      color: 'orange',
      bgColor: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      borderColor: 'border-orange-300',
      bgHover: 'hover:bg-orange-50',
      logo: partnerLogo,
      altText: 'RoadExpress Partner Logo'
    },
    {
      id: 2,
      name: 'RoadExpress Freight',
      description: 'For shippers and freight booking',
      color: 'blue',
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      borderColor: 'border-blue-300',
      bgHover: 'hover:bg-blue-50',
      logo: freightLogo,
      altText: 'RoadExpress Freight Logo'
    }
  ];

  // Handle scroll effect for header background and visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY);

      // Only update if scroll difference is significant (prevents jittery behavior)
      if (scrollDifference < 10) return;

      // Show header when at top of page
      if (currentScrollY < 100) {
        setIsVisible(true);
        setIsScrolled(false);
      } else {
        // Show header when scrolling up, hide when scrolling down
        if (currentScrollY < lastScrollY) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
          // Close mobile menu when hiding header while scrolling down
          if (isMobileMenuOpen) {
            setIsMobileMenuOpen(false);
          }
        }
        setIsScrolled(true);
      }

      setLastScrollY(currentScrollY);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobileMenuOpen]);

  // Handle mobile menu toggle
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Handle modal toggle
  const toggleModal = useCallback(() => {
    setIsModalOpen(prev => !prev);
    closeMobileMenu();
    // Reset form when opening modal
    if (!isModalOpen) {
      setSelectedApp('');
      setPhoneNumber('');
      setSubmitStatus(null);
    }
  }, [closeMobileMenu, isModalOpen]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedApp('');
    setPhoneNumber('');
    setSubmitStatus(null);
  }, []);

  // Handle logo error
  const handleLogoError = useCallback(() => {
    setLogoError(true);
    console.error('Failed to load company logo');
  }, []);

  const handleAppLogoError = useCallback((appId) => {
    setLogoErrors(prev => ({
      ...prev,
      [appId]: true
    }));
  }, []);

  // Handle navigation click
  const handleNavClick = useCallback((href) => {
    closeMobileMenu();
    console.log(`Navigating to: ${href}`);
  }, [closeMobileMenu]);

  // Validate phone number (Indian format)
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers;
    }
    return numbers.slice(0, 10);
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  // Handle app selection
  const handleAppSelect = (appId) => {
    setSelectedApp(appId);
  };

  // Send SMS with download link
  const sendDownloadSMS = async (phone, appName) => {
    const apiData = {
      ph_no: phone,
      appName: appName
    };

    try {
      const response = await fetch('https://api.roadexpressfreight.com/api/v1/web/webAppDownload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('SMS sending failed:', error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedApp || !validatePhoneNumber(phoneNumber)) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendDownloadSMS(phoneNumber, selectedApp); // selectedApp is now 1 or 2

      if (result.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          closeModal();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal/menu on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (isModalOpen) {
          closeModal();
        } else if (isMobileMenuOpen) {
          closeMobileMenu();
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isModalOpen, isMobileMenuOpen, closeModal, closeMobileMenu]);

  // Prevent body scroll when modal or mobile menu is open
  useEffect(() => {
    if (isModalOpen || isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen, isMobileMenuOpen]);

  return (
    <>
      <div
        className={`fixed top-4 left-4 right-4 z-50 transition-transform duration-300 ease-out ${isVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
      >
        <header
          className={`transition-all duration-500 ease-out rounded-xl ${isScrolled
            ? 'bg-black/60 backdrop-blur-md'
            : 'bg-transparent'
            }`}
          role="banner"
        >
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center flex-shrink-0">
                <a
                  href="/"
                  className="flex items-center focus:outline-none rounded-lg p-1"
                  aria-label="RoadExpress - Home"
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="RoadExpress - Leading Logistics Company in India"
                      className="h-10 sm:h-12 w-auto transition-transform duration-300 hover:scale-105"
                      onError={handleLogoError}
                      loading="eager"
                    />
                  ) : (
                    <div className="h-10 sm:h-12 flex items-center justify-center bg-[#F05A28] text-white font-bold text-lg px-4 rounded-lg">
                      RE
                    </div>
                  )}
                </a>
              </div>

              {/* Navigation - Desktop */}
              <nav
                className="hidden lg:flex items-center space-x-6 xl:space-x-8"
                role="navigation"
                aria-label="Main navigation"
              >
                {navigationItems.map((item, index) => (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={() => handleNavClick(item.href)}
                    className="relative text-white hover:text-[#F05A28] font-medium text-sm xl:text-base transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-[#F05A28]/50 rounded px-2 py-1"
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.5)'
                    }}
                    aria-label={item.ariaLabel}
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-2 w-0 h-0.5 bg-[#F05A28] transition-all duration-300 group-hover:w-[calc(100%-1rem)]"></span>
                  </a>
                ))}
              </nav>

              {/* Download App Button - Desktop only */}
              <div className="hidden lg:flex items-center">
                <button
                  onClick={toggleModal}
                  className="bg-[#F05A28] text-white py-2 sm:py-2.5 px-4 sm:px-6 rounded-lg hover:bg-[#e04a18] hover:shadow-lg transform transition-all duration-300 hover:-translate-y-0.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#F05A28] focus:ring-opacity-50 focus:ring-offset-2 focus:ring-offset-transparent flex items-center space-x-2"
                  aria-label="Download RoadExpress mobile apps"
                >
                  <Download className="w-4 h-4" />
                  <span>Download App</span>
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden text-white hover:text-[#F05A28] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#F05A28] focus:ring-opacity-50 rounded-md p-2"
                aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <div className="relative w-6 h-6">
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 top-3' : 'top-1'
                      }`}
                    style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.5)' }}
                  ></span>
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 top-3 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                      }`}
                    style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.5)' }}
                  ></span>
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 top-3' : 'top-5'
                      }`}
                    style={{ textShadow: isScrolled ? 'none' : '0 1px 2px rgba(0,0,0,0.5)' }}
                  ></span>
                </div>
              </button>
            </div>

            {/* Mobile Menu */}
            <div
              id="mobile-menu"
              className={`lg:hidden overflow-hidden transition-all duration-500 ease-out ${isMobileMenuOpen
                ? 'max-h-[500px] opacity-100 mt-4'
                : 'max-h-0 opacity-0 mt-0'
                }`}
              aria-hidden={!isMobileMenuOpen}
            >
              <div className="bg-black/60 backdrop-blur-lg rounded-xl border border-white/10">
                <nav
                  className="flex flex-col space-y-1 py-4 px-4"
                  role="navigation"
                  aria-label="Mobile navigation"
                >
                  {navigationItems.map((item, index) => (
                    <a
                      key={item.id}
                      href={item.href}
                      onClick={() => handleNavClick(item.href)}
                      className="text-white hover:text-[#F05A28] hover:bg-white/10 font-medium text-base transition-all duration-300 py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F05A28]/50"
                      style={{
                        opacity: isMobileMenuOpen ? 1 : 0,
                        transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                        transitionDelay: isMobileMenuOpen ? `${index * 0.1}s` : '0s'
                      }}
                      aria-label={item.ariaLabel}
                    >
                      {item.name}
                    </a>
                  ))}

                  {/* Mobile Download App Button */}
                  <div className="pt-4 mt-4 border-t border-white/20">
                    <button
                      onClick={toggleModal}
                      className="w-full bg-[#F05A28] text-white py-3 px-6 rounded-lg hover:bg-[#e04a18] transition-all duration-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#F05A28] focus:ring-opacity-50 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                      style={{
                        opacity: isMobileMenuOpen ? 1 : 0,
                        transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
                        transitionDelay: isMobileMenuOpen ? '0.5s' : '0s'
                      }}
                      aria-label="Download RoadExpress mobile apps"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download App</span>
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
        )}
      </div>

      {/* Download App Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Modal Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
            aria-hidden="true"
          />

          {/* Modal Content */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100"
            role="dialog"
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 id="modal-title" className="text-2xl font-bold text-gray-900">
                  Get Download Link
                </h2>
                <p id="modal-description" className="text-gray-600 mt-1">
                  We'll send you the app download link via SMS
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Success/Error Message */}
              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${submitStatus === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                  {submitStatus === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-semibold text-sm">
                      {submitStatus === 'success'
                        ? 'SMS Sent Successfully!'
                        : 'Failed to Send SMS'
                      }
                    </p>
                    <p className="text-xs">
                      {submitStatus === 'success'
                        ? 'Check your phone for the download link.'
                        : 'Please check your phone number and try again.'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* App Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select App *
                </label>
                <div className="space-y-3">
                  {appOptions.map((app) => (
                    <div
                      key={app.id}
                      className={`border rounded-xl p-4 cursor-pointer transition-all duration-300 ${selectedApp === app.id
                        ? `${app.borderColor} ${app.bgHover} ring-2 ring-${app.color}-200`
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      onClick={() => handleAppSelect(app.id)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                          {!logoErrors[app.id] ? (
                            <img
                              src={app.logo}
                              alt={app.altText}
                              className="w-full h-full object-contain"
                              onError={() => handleAppLogoError(app.id)}
                            />
                          ) : (
                            <div className={`w-full h-full ${app.bgColor} flex items-center justify-center text-white font-bold text-xs`}>
                              {app.id === 1 ? 'RP' : 'RF'}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {app.name}
                          </h3>
                          <p className="text-xs text-gray-600">
                            {app.description}
                          </p>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 ${selectedApp === app.id
                          ? `border-${app.color}-500 bg-${app.color}-500`
                          : 'border-gray-300'
                          }`}>
                          {selectedApp === app.id && (
                            <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phone Number Input */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Phone Number *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">
                    +91
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="9876543210"
                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent transition-all text-sm ${phoneNumber && !validatePhoneNumber(phoneNumber)
                      ? 'border-red-300 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-blue-200'
                      }`}
                    maxLength="10"
                    required
                  />
                </div>
                {phoneNumber && !validatePhoneNumber(phoneNumber) && (
                  <p className="text-red-600 text-xs mt-2">
                    Please enter a valid 10-digit Indian mobile number
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedApp || !validatePhoneNumber(phoneNumber)}
                className={`w-full py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center space-x-2 ${isSubmitting || !selectedApp || !validatePhoneNumber(phoneNumber)
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-[#F05A28] text-white hover:bg-[#e04a18] hover:shadow-lg'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending SMS...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Download Link</span>
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-500 mt-4">
                Standard SMS rates may apply. Available for Indian mobile numbers only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "RoadExpress",
          "description": "Leading logistics and transportation company in India",
          "url": "https://roadexpressfreight.com",
          "logo": "https://roadexpressfreight.com/logo.png",
          "sameAs": [
            "https://facebook.com/roadexpress.in",
            "https://linkedin.com/company/roadexpress",
            "https://x.com/road_express?s=08"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-844-773-8498",
            "contactType": "customer service"
          }
        })}
      </script>
    </>
  );
};

export default Header;